<template>
  <div class="auth-layout">
    <section class="auth-story">
      <div class="story-grid" aria-hidden="true" />
      <div class="story-glow story-glow-primary" aria-hidden="true" />
      <div class="story-glow story-glow-secondary" aria-hidden="true" />
      <BrandLogo to="/try" />
      <div class="auth-story-main">
        <div class="auth-story-copy">
          <span class="status-pill"><span class="status-dot" />FindBao Workspace</span>
          <h1>{{ t('app.try.title') }}</h1>
          <p>{{ t('app.search.subtitle') }}</p>
        </div>

        <div class="auth-showcase" aria-hidden="true">
          <span class="showcase-halo" />
          <img class="showcase-product" src="/images/auth-chair.png" alt="" />
          <i class="scan-corner scan-corner-tl" />
          <i class="scan-corner scan-corner-tr" />
          <i class="scan-corner scan-corner-bl" />
          <i class="scan-corner scan-corner-br" />
          <div class="showcase-bars"><i /><i /><i /></div>
          <svg class="showcase-signal" viewBox="0 0 86 36" aria-hidden="true">
            <path d="M2 30l13-12 11 7 16-18 12 13 14-9 16 5" />
          </svg>
          <span class="showcase-focus" />
        </div>
      </div>
      <a :href="homeUrl" target="_blank" rel="noopener">{{ homeUrl }} ↗</a>
    </section>
    <section class="auth-panel">
      <div class="auth-tools"><LocaleSwitcher /><ThemeSwitcher /></div>
      <div class="auth-card"><RouterView /></div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import BrandLogo from '@/components/BrandLogo.vue'
import LocaleSwitcher from '@/components/LocaleSwitcher.vue'
import ThemeSwitcher from '@/components/ThemeSwitcher.vue'
const { t } = useI18n()
const homeUrl = import.meta.env.VITE_APP_HOME_DOMAIN
</script>

<style scoped>
.auth-layout {
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(360px, 0.9fr) minmax(480px, 1.1fr);
}
.auth-story {
  --story-bg-start: #0b2a38;
  --story-bg-end: #061924;
  --story-text: #f5fbfb;
  --story-muted: #a8c0c6;
  --story-panel: rgba(5, 28, 39, 0.74);
  --story-panel-soft: rgba(255, 255, 255, 0.055);
  --story-border: rgba(181, 221, 217, 0.28);
  --story-grid-color: rgba(214, 237, 235, 0.045);
  min-height: 100vh;
  padding: clamp(28px, 4vw, 64px);
  position: relative;
  display: flex;
  flex-direction: column;
  isolation: isolate;
  overflow: hidden;
  background: linear-gradient(145deg, var(--story-bg-start), var(--story-bg-end));
  color: var(--story-text);
}
:global(html.dark) .auth-story {
  --story-bg-start: #18132d;
  --story-bg-end: #080c18;
  --story-muted: #bdb5d5;
  --story-panel: rgba(13, 12, 29, 0.76);
  --story-border: rgba(196, 181, 253, 0.27);
  --story-grid-color: rgba(221, 214, 254, 0.045);
}
:global(html.theme-orange) .auth-story {
  --story-bg-start: #442314;
  --story-bg-end: #21130c;
  --story-muted: #dbc0aa;
  --story-panel: rgba(42, 22, 12, 0.76);
  --story-border: rgba(244, 184, 133, 0.29);
  --story-grid-color: rgba(255, 226, 201, 0.045);
}
:global(html.theme-blue) .auth-story {
  --story-bg-start: #123c69;
  --story-bg-end: #071c32;
  --story-muted: #b6cce5;
  --story-panel: rgba(6, 29, 52, 0.76);
  --story-border: rgba(174, 207, 245, 0.29);
  --story-grid-color: rgba(214, 232, 255, 0.05);
}
.story-grid {
  position: absolute;
  inset: 0;
  z-index: -3;
  background-image:
    linear-gradient(var(--story-grid-color) 1px, transparent 1px),
    linear-gradient(90deg, var(--story-grid-color) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(to bottom, transparent 2%, black 25%, black 83%, transparent);
  pointer-events: none;
}
.story-glow {
  position: absolute;
  z-index: -2;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(8px);
}
.story-glow-primary {
  width: 420px;
  height: 420px;
  inset-inline-start: -210px;
  bottom: -210px;
  background: color-mix(in srgb, var(--fb-coral) 31%, transparent);
}
.story-glow-secondary {
  width: 310px;
  height: 310px;
  inset-inline-end: -150px;
  top: 7%;
  background: color-mix(in srgb, var(--fb-coral) 17%, #ef6b50 12%, transparent);
}
.auth-story :deep(.brand-copy small) {
  color: var(--story-muted);
}
.auth-story :deep(.brand-copy strong) {
  color: var(--story-text);
}
.auth-story > :deep(.brand-lockup),
.auth-story-main,
.auth-story > a {
  position: relative;
  z-index: 1;
}
.auth-story-main {
  width: min(100%, 620px);
  margin-block: auto;
}
.auth-story-copy {
  max-width: 620px;
}
.auth-story .status-pill {
  border-color: color-mix(in srgb, var(--fb-coral) 34%, white 12%, transparent);
  background: color-mix(in srgb, var(--story-panel) 64%, white 10%);
  color: color-mix(in srgb, var(--fb-coral) 68%, white);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.13);
  backdrop-filter: blur(12px);
}
.auth-story .status-dot,
.showcase-bars i {
  background: var(--fb-coral);
  box-shadow: 0 0 0 5px color-mix(in srgb, var(--fb-coral) 13%, transparent);
}
.auth-story h1 {
  margin: 22px 0 18px;
  font-size: clamp(40px, 5vw, 60px);
  line-height: 1.18;
  letter-spacing: -0.055em;
}
.auth-story p {
  max-width: 560px;
  color: var(--story-muted);
  font-size: 16px;
  line-height: 1.75;
}
.auth-showcase {
  width: min(100%, 540px);
  aspect-ratio: 1.62;
  margin-top: clamp(26px, 4vh, 42px);
  position: relative;
  overflow: hidden;
  border: 1px solid var(--story-border);
  border-radius: 20px;
  background:
    linear-gradient(var(--story-grid-color) 1px, transparent 1px),
    linear-gradient(90deg, var(--story-grid-color) 1px, transparent 1px),
    radial-gradient(circle at 46% 47%, var(--story-panel-soft), transparent 47%), var(--story-panel);
  background-size:
    36px 36px,
    36px 36px,
    auto,
    auto;
  box-shadow:
    0 28px 64px rgba(0, 0, 0, 0.24),
    0 1px 0 rgba(255, 255, 255, 0.08) inset;
  backdrop-filter: blur(18px);
}
.auth-showcase::before {
  position: absolute;
  inset: 0;
  z-index: 2;
  background:
    linear-gradient(
      100deg,
      transparent 0 47%,
      color-mix(in srgb, var(--fb-coral) 18%, transparent) 49.8%,
      transparent 52% 100%
    ),
    linear-gradient(
      165deg,
      transparent 0 58%,
      color-mix(in srgb, var(--fb-coral) 12%, transparent) 60%,
      transparent 63% 100%
    );
  content: '';
  pointer-events: none;
}
.auth-showcase::after {
  position: absolute;
  inset-inline: 9%;
  bottom: 7%;
  height: 12%;
  border-radius: 50%;
  background: color-mix(in srgb, var(--fb-coral) 14%, transparent);
  content: '';
  filter: blur(16px);
}
.showcase-halo {
  position: absolute;
  width: 49%;
  aspect-ratio: 1;
  inset-inline-start: 21%;
  top: 11%;
  border: 1px solid color-mix(in srgb, var(--fb-coral) 12%, transparent);
  border-radius: 50%;
  background: radial-gradient(
    circle,
    color-mix(in srgb, var(--fb-coral) 12%, transparent),
    transparent 68%
  );
  box-shadow: 0 0 55px color-mix(in srgb, var(--fb-coral) 9%, transparent);
}
.showcase-halo::before,
.showcase-halo::after {
  position: absolute;
  inset: 16%;
  border: 1px solid color-mix(in srgb, var(--story-border) 42%, transparent);
  border-radius: 50%;
  content: '';
}
.showcase-halo::after {
  inset: 32%;
}
.showcase-product {
  position: absolute;
  z-index: 1;
  width: 54%;
  height: 82%;
  inset-inline-start: 16%;
  bottom: 3%;
  object-fit: contain;
  filter: drop-shadow(0 18px 22px rgba(0, 0, 0, 0.24));
}
.scan-corner {
  position: absolute;
  z-index: 3;
  width: 20px;
  height: 20px;
  border-color: var(--fb-coral);
  border-style: solid;
  opacity: 0.84;
  filter: drop-shadow(0 0 5px color-mix(in srgb, var(--fb-coral) 48%, transparent));
}
.scan-corner-tl {
  inset: 17px auto auto 17px;
  border-width: 2px 0 0 2px;
}
.scan-corner-tr {
  inset: 17px 17px auto auto;
  border-width: 2px 2px 0 0;
}
.scan-corner-bl {
  inset: auto auto 17px 17px;
  border-width: 0 0 2px 2px;
}
.scan-corner-br {
  inset: auto 17px 17px auto;
  border-width: 0 2px 2px 0;
}
.showcase-bars {
  position: absolute;
  z-index: 3;
  width: 68px;
  inset-inline-end: 11%;
  top: 18%;
  display: grid;
  gap: 9px;
}
.showcase-bars i {
  width: 100%;
  height: 7px;
  border-radius: 999px;
  opacity: 0.1;
  box-shadow: none;
}
.showcase-bars i:nth-child(2) {
  width: 82%;
}
.showcase-bars i:nth-child(3) {
  width: 58%;
}
.showcase-signal {
  position: absolute;
  z-index: 3;
  width: 78px;
  inset-inline-end: 10%;
  bottom: 24%;
  overflow: visible;
  opacity: 0.18;
}
.showcase-signal path {
  fill: none;
  stroke: var(--fb-coral);
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
}
.showcase-focus {
  position: absolute;
  z-index: 4;
  width: 64px;
  height: 64px;
  inset-inline-end: 10%;
  bottom: 13%;
  background:
    linear-gradient(var(--fb-coral), var(--fb-coral)) left top / 18px 3px no-repeat,
    linear-gradient(var(--fb-coral), var(--fb-coral)) left top / 3px 18px no-repeat,
    linear-gradient(var(--fb-coral), var(--fb-coral)) right top / 18px 3px no-repeat,
    linear-gradient(var(--fb-coral), var(--fb-coral)) right top / 3px 18px no-repeat,
    linear-gradient(var(--fb-coral), var(--fb-coral)) left bottom / 18px 3px no-repeat,
    linear-gradient(var(--fb-coral), var(--fb-coral)) left bottom / 3px 18px no-repeat,
    linear-gradient(var(--fb-coral), var(--fb-coral)) right bottom / 18px 3px no-repeat,
    linear-gradient(var(--fb-coral), var(--fb-coral)) right bottom / 3px 18px no-repeat;
  filter: drop-shadow(0 0 7px color-mix(in srgb, var(--fb-coral) 42%, transparent));
}
.showcase-focus::before,
.showcase-focus::after {
  position: absolute;
  inset: 50% auto auto 50%;
  width: 16px;
  height: 2px;
  border-radius: 99px;
  background: var(--fb-coral);
  content: '';
  transform: translate(-50%, -50%);
}
.showcase-focus::after {
  transform: translate(-50%, -50%) rotate(90deg);
}
.auth-story > a {
  width: fit-content;
  color: var(--story-muted);
  font-size: 12px;
  transition: color 0.2s ease;
}
.auth-story > a:hover {
  color: color-mix(in srgb, var(--fb-coral) 62%, white);
}
.auth-panel {
  position: relative;
  padding: 90px 24px 40px;
  display: grid;
  place-items: center;
  background: var(--fb-paper);
}
.auth-tools {
  position: absolute;
  top: 20px;
  inset-inline-end: 24px;
  display: flex;
  gap: 8px;
}
.auth-card {
  width: min(470px, 100%);
}
@media (max-width: 880px) {
  .auth-layout {
    display: block;
  }
  .auth-story {
    min-height: 260px;
    padding: 25px 22px;
  }
  .auth-story-copy {
    margin: 38px 0 0;
  }
  .auth-story h1 {
    margin: 13px 0 10px;
    font-size: 34px;
  }
  .auth-story p {
    margin-bottom: 0;
    font-size: 13px;
  }
  .auth-showcase {
    display: none;
  }
  .auth-story > a {
    display: none;
  }
  .auth-panel {
    min-height: calc(100vh - 260px);
    padding-top: 80px;
  }
}
</style>
